import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import HomeHeader from '../home-page/components/HomeHeader';
import FeaturesShowcase from '../home-page/components/FeaturesShowcase';
import DetailedFeaturesGrid from '../home-page/components/DetailedFeaturesGrid';
import WorkflowSection from '../home-page/components/WorkflowSection';
import TestimonialsSection from '../home-page/components/TestimonialsSection';
import PricingSection from '../home-page/components/PricingSection';
import FAQSection from '../home-page/components/FAQSection';
import Icon from '../../components/AppIcon';
import { FEATURE_DEFINITIONS, formatLimit } from '../../config/planFeatures';
import { getCanonicalPricingTiers, getPricingTierKey } from '../../config/pricingTiers';
import { adminService } from '../../services/adminService';
import { COMPARISON_DATA } from '../../config/comparisonPages.mjs';

const PageShell = ({ title, description, path, children }) => {
  const canonicalUrl = `https://webforms.biz${path}`;

  return (
  <div className="min-h-screen bg-white">
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
    </Helmet>
    <HomeHeader />
    <main className="pt-28 pb-20">
      {children}
    </main>
  </div>
  );
};

export const AboutPage = () => (
  <PageShell
    title="About WebForms.biz | Form Building for Growing Teams"
    description="Learn why WebForms.biz gives marketing, sales, operations, and agency teams one place to build, publish, and improve forms."
    path="/about"
  >
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-6">
        <Icon name="Building2" size={14} />
        <span>About WebForms.biz</span>
      </div>
      <h1 className="text-4xl sm:text-6xl font-heading font-bold text-gray-900 leading-tight mb-6">
        Forms should help your team move faster
      </h1>
      <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-12">
        WebForms.biz brings form building, publishing, submissions, analytics, payments, and optimization into one workspace for teams that want control without a custom development project.
      </p>

      <div className="grid md:grid-cols-3 gap-5 mb-14">
        {[
          ['MousePointerClick', 'Build visually', 'Create forms with drag-and-drop fields, conditional logic, and reusable themes.'],
          ['ChartNoAxesCombined', 'Improve with data', 'Review submissions and analytics, then compare form variants with A/B testing.'],
          ['Workflow', 'Fit your workflow', 'Publish with a link or embed and connect other tools with Zapier, webhooks, and API access.'],
        ].map(([icon, heading, copy]) => (
          <article key={heading} className="rounded-2xl border border-gray-200 p-6">
            <Icon name={icon} size={22} className="text-[#0e7572] mb-4" />
            <h2 className="font-heading font-bold text-lg text-gray-900 mb-2">{heading}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">{copy}</p>
          </article>
        ))}
      </div>

      <section className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8 lg:gap-14 items-center mb-14" aria-labelledby="founder-story-heading">
        <div
          className="aspect-[4/5] max-w-sm w-full mx-auto rounded-3xl border-2 border-dashed border-teal-200 bg-teal-50/60 flex flex-col items-center justify-center text-center p-8"
          data-founder-photo-slot
          aria-label="Founder photo placeholder"
        >
          <div className="w-24 h-24 rounded-full bg-white border border-teal-100 flex items-center justify-center mb-5 shadow-sm">
            <Icon name="UserRound" size={42} className="text-[#0e7572]" />
          </div>
          <p className="text-sm font-semibold text-[#0e7572]">Founder photo</p>
          <p className="text-xs text-gray-500 mt-2">Image coming soon</p>
        </div>

        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-5">
            <Icon name="Quote" size={14} />
            <span>Why I built WebForms.biz</span>
          </div>
          <h2 id="founder-story-heading" className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 leading-tight mb-5">
            Meet Steven Smith
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6">
            As an online entrepreneur, digital marketer, SEO expert, and web developer, I often found myself paying over the odds for a web form builder that just “worked.” Many times I bought and used what was available, but I was never truly satisfied with what was out there. So after developing web apps for over three years, I finally decided to build an easy-to-manage, easy-to-use, and simple-to-implement web form builder that is customizable, versatile, and offers more features for less than any so-called competitor. That was the primary reasoning behind offering our Free Web Form Builder plan, built on a “use it and prove it” mantra. Try it, use it, and you tell me what you think—simple, straightforward, and honest.
          </p>
          <a
            href="https://www.linkedin.com/in/vancoreseo/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0e7572] hover:text-[#0a5f5c] transition-colors"
          >
            <Icon name="Linkedin" size={17} />
            Connect with Steven on LinkedIn
            <Icon name="ExternalLink" size={14} />
          </a>
        </div>
      </section>

      <div className="rounded-3xl bg-[#0a1628] p-8 sm:p-10 text-white">
        <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-3">See whether it fits your workflow</h2>
        <p className="text-gray-300 mb-6 max-w-2xl">
          Start on the free plan, review the product and policies, and contact support if you need help evaluating a specific use case.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/authentication" className="px-5 py-3 rounded-xl bg-[#0e7572] hover:bg-[#0a5f5c] font-semibold">Start free</Link>
          <Link to="/pricing" className="px-5 py-3 rounded-xl border border-white/20 hover:bg-white/10 font-semibold">View pricing</Link>
        </div>
      </div>
    </section>
  </PageShell>
);

export const FeaturesPage = () => (
  <PageShell
    title="WebForms.biz Features | Forms, Analytics, Payments, and More"
    description="Explore WebForms.biz features for building, publishing, analyzing, and optimizing forms without waiting for engineering support."
    path="/features"
  >
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-6">
          <Icon name="Sparkles" size={14} />
          <span>One workspace for every form workflow</span>
        </div>
        <h1 className="text-4xl sm:text-6xl font-heading font-bold text-gray-900 leading-tight mb-6">
          Build forms that keep moving after publish
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
          Create forms visually, publish them anywhere, understand every response, and connect the tools your team already uses.
        </p>
      </div>
    </section>
    <FeaturesShowcase />
    <DetailedFeaturesGrid />
    <WorkflowSection />
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
      <div className="rounded-3xl bg-[#0a1628] p-8 sm:p-10 text-white flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-2">Ready to build your first form?</h2>
          <p className="text-gray-300">Start on the free plan and add more capability as your workflow grows.</p>
        </div>
        <Link to="/authentication" className="px-5 py-3 rounded-xl bg-[#0e7572] hover:bg-[#0a5f5c] font-semibold whitespace-nowrap">Start free</Link>
      </div>
    </section>
  </PageShell>
);

export const CustomersPage = () => (
  <PageShell
    title="WebForms.biz Workflows | Forms for Marketing, Agencies, and Operations"
    description="See how marketing, agency, and operations teams can use WebForms.biz to build forms, collect responses, and improve workflows."
    path="/customers"
  >
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-2">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-6">
          <Icon name="Users" size={14} />
          <span>Workflows for growing teams</span>
        </div>
        <h1 className="text-4xl sm:text-6xl font-heading font-bold text-gray-900 leading-tight mb-6">
          One platform, different ways to work
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
          Explore the workflows WebForms.biz supports today. These are product use cases, not customer testimonials or performance claims.
        </p>
      </div>
    </section>
    <TestimonialsSection />
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
      <div className="rounded-3xl border border-teal-100 bg-teal-50/70 p-8 sm:p-10">
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-3">See whether it fits your workflow</h2>
        <p className="text-gray-600 mb-6 max-w-2xl">
          Browse templates, review the current plans, and try WebForms.biz before you commit to a paid subscription.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/templates-gallery" className="px-5 py-3 rounded-xl bg-[#0e7572] hover:bg-[#0a5f5c] text-white font-semibold">Browse templates</Link>
          <Link to="/pricing" className="px-5 py-3 rounded-xl border border-gray-300 hover:bg-white font-semibold text-gray-800">View pricing</Link>
        </div>
      </div>
    </section>
  </PageShell>
);

export const PricingPage = () => (
  <PageShell
    title="WebForms.biz Pricing | Transparent Form Builder Plans"
    description="Compare WebForms.biz plans with clear form, submission, seat, storage, and feature limits. Start free, or save with annual billing."
    path="/pricing"
  >
    <PricingSection />
    <FAQSection />
  </PageShell>
);

const COMPARISON_PLANS = getCanonicalPricingTiers();

const isFeatureIncluded = (plan, featureKey) => {
  const definition = FEATURE_DEFINITIONS.find((feature) => feature.key === featureKey);
  if (!definition) return false;

  const configured = plan?.featureEntitlements?.[featureKey];
  if (typeof configured?.included === 'boolean') return configured.included;

  const saved = Array.isArray(plan?.features)
    ? plan.features.find((feature) => (feature?.key || feature?.featureKey) === featureKey)
    : null;
  if (saved?.included !== undefined) return saved.included !== false;

  return definition.plans[getPricingTierKey(plan)] !== false;
};

const joinPlanLabels = (plans) => {
  const labels = plans.map((plan) => plan.tierName);
  if (labels.length <= 1) return labels[0] || 'no active plan';
  return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`;
};

const featureAvailability = (plans, featureKey) => {
  const includedPlans = plans.filter((plan) => isFeatureIncluded(plan, featureKey));
  if (includedPlans.length === 0) return 'Not included in the active catalog';
  if (includedPlans.length === plans.length) return 'Included on every active plan';
  return `Included on ${joinPlanLabels(includedPlans)}`;
};

const unlimitedSeatAvailability = (plans) => {
  const unlimitedPlans = plans.filter((plan) => Number(plan.maxTeamMembers) === -1);
  return unlimitedPlans.length
    ? `${joinPlanLabels(unlimitedPlans)} include unlimited team seats`
    : 'Compare the current seat allowance shown for each plan';
};

const buildComparisonRows = (plans) => [
  { feature: 'Form building', webforms: featureAvailability(plans, 'form_builder'), compare: 'Editor, field types, and reusable content' },
  { feature: 'Multi-step flows', webforms: featureAvailability(plans, 'multi_step'), compare: 'Pages, conversational flow, and branching' },
  { feature: 'Analytics', webforms: `Basic analytics: ${featureAvailability(plans, 'basic_analytics').toLowerCase()}; advanced reports: ${featureAvailability(plans, 'advanced_analytics').toLowerCase()}`, compare: 'Funnel, response, and conversion reporting' },
  { feature: 'A/B testing', webforms: featureAvailability(plans, 'ab_testing'), compare: 'Experiment support and variant reporting' },
  { feature: 'Team seats', webforms: unlimitedSeatAvailability(plans), compare: 'Seat limits and collaborator permissions' },
  { feature: 'White-label workflows', webforms: featureAvailability(plans, 'white_label'), compare: 'Branding, custom domains, and client workspaces' },
  { feature: 'Payments', webforms: featureAvailability(plans, 'payment_forms'), compare: 'Payment providers, fees, and checkout controls' },
  { feature: 'Integrations', webforms: `Core integrations: ${featureAvailability(plans, 'core_integrations').toLowerCase()}; Zapier/webhooks: ${featureAvailability(plans, 'webhooks').toLowerCase()}; REST API: ${featureAvailability(plans, 'api_access').toLowerCase()}`, compare: 'Native apps, automation, webhooks, and API access' },
  { feature: 'Publishing', webforms: featureAvailability(plans, 'embed'), compare: 'Share, embed, domain, and display options' },
  { feature: 'Submission limits', webforms: plans.map((plan) => `${plan.tierName}: ${formatLimit(plan.maxSubmissions)}/mo`).join(' · '), compare: 'Monthly response or submission allowance' },
];

const DifferentiatorCards = ({ plans, catalogStatus }) => (
  <div className="mb-12">
    <p className="text-sm text-gray-500 mb-4">
      {catalogStatus === 'live'
        ? 'Plan qualifiers below reflect the current active WebForms.biz catalog.'
        : 'Plan qualifiers below use catalog defaults; confirm the latest offer on the pricing page.'}
    </p>
    <div className="grid md:grid-cols-3 gap-4">
      {[
        ['UsersRound', 'Unlimited seats', `${unlimitedSeatAvailability(plans)}.`],
        ['Split', 'A/B testing included', `${featureAvailability(plans, 'ab_testing')}.`],
        ['PanelTop', 'White-label workflows', `${featureAvailability(plans, 'white_label')}.`],
      ].map(([icon, heading, copy]) => (
        <article key={heading} className="rounded-2xl border border-teal-100 bg-teal-50/60 p-5">
          <Icon name={icon} size={21} className="text-[#0e7572] mb-4" />
          <h2 className="text-lg font-heading font-bold text-gray-900 mb-2">{heading}</h2>
          <p className="text-sm text-gray-600 leading-relaxed">{copy}</p>
        </article>
      ))}
    </div>
  </div>
);

const ComparisonTable = ({ competitor, plans, catalogStatus }) => {
  const scrollHelpId = `${competitor.toLowerCase()}-comparison-scroll-help`;
  const rows = buildComparisonRows(plans);

  return (
  <div className="mb-12">
    <div className="mb-5">
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-2">Feature comparison</h2>
      <p className="text-gray-600">
        {catalogStatus === 'live'
          ? 'WebForms.biz details come from the current active plan catalog.'
          : 'WebForms.biz details currently use catalog defaults; confirm the latest offer on the pricing page.'}{' '}
        Verify {competitor}’s current offer before making a final decision.
      </p>
      <p id={scrollHelpId} className="text-sm text-gray-500 mt-2">On smaller screens, scroll the table horizontally to compare every column.</p>
    </div>
    <div
      className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0e7572] focus:ring-offset-2"
      role="region"
      tabIndex={0}
      aria-label={`Scrollable WebForms.biz and ${competitor} feature comparison`}
      aria-describedby={scrollHelpId}
    >
      <table className="w-full min-w-[760px] text-sm border-collapse">
        <caption className="sr-only">{`WebForms.biz and ${competitor} feature comparison`}</caption>
        <thead>
          <tr className="bg-[#0a1628] text-white">
            <th scope="col" className="py-4 px-5 text-left font-semibold w-[24%]">Capability</th>
            <th scope="col" className="py-4 px-5 text-left font-semibold w-[38%]">WebForms.biz</th>
            <th scope="col" className="py-4 px-5 text-left font-semibold w-[38%]">{competitor} — verify current offer</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.feature} className={`border-t border-gray-200 align-top ${index % 2 ? 'bg-gray-50' : 'bg-white'}`}>
              <th scope="row" className="py-4 px-5 text-left font-semibold text-gray-900">{row.feature}</th>
              <td className="py-4 px-5 text-gray-700">
                <span className="inline-flex gap-2 items-start">
                  <Icon name="CheckCircle2" size={17} className="text-[#0e7572] flex-shrink-0 mt-0.5" />
                  <span>{row.webforms}</span>
                </span>
              </td>
              <td className="py-4 px-5 text-gray-600">{row.compare}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};

const PricingComparison = ({ competitor, plans, catalogStatus }) => (
  <section className="mb-12" aria-labelledby="pricing-comparison-heading">
    <div className="mb-5">
      <h2 id="pricing-comparison-heading" className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-2">Pricing comparison</h2>
      <p className="text-gray-600">
        {catalogStatus === 'live'
          ? 'WebForms.biz publishes its current active catalog below.'
          : 'WebForms.biz catalog defaults are shown below; confirm the latest offer on the pricing page.'}{' '}
        {competitor} pricing can change, so check the provider directly rather than relying on a stale side-by-side number.
      </p>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
      {plans.map((plan) => {
        return (
          <article key={plan.planKey} className="rounded-2xl border border-gray-200 bg-white p-5">
            <h3 className="font-heading font-bold text-gray-900">{plan.tierName}</h3>
            <p className="text-2xl font-heading font-bold text-[#0e7572] mt-3">
              {plan.price === 0 ? 'Free' : `$${plan.price}/mo`}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {plan.annualPrice === 0 ? 'No annual charge' : `$${plan.annualMonthlyPrice}/mo billed annually`}
            </p>
            <p className="text-xs text-gray-500 mt-4">
              {formatLimit(plan.maxSubmissions)} submissions/mo · {formatLimit(plan.maxTeamMembers)} seats
            </p>
          </article>
        );
      })}
    </div>
    <p className="text-sm text-gray-500">
      Compare the same items on {competitor}’s current pricing page: response limits, seats, integrations, payment features, branding, and annual billing.
    </p>
  </section>
);

const MigrationGuidance = ({ competitor, steps }) => (
  <section className="mb-12" aria-labelledby="migration-heading">
    <div className="mb-5">
      <h2 id="migration-heading" className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-2">Migration guidance from {competitor}</h2>
      <p className="text-gray-600">A controlled migration keeps your forms live while you validate the new workflow.</p>
    </div>
    <ol className="grid md:grid-cols-2 gap-4">
      {steps.map((step, index) => (
        <li key={step} className="flex gap-4 rounded-2xl border border-gray-200 p-5 bg-white">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-50 text-[#0e7572] font-bold text-sm flex-shrink-0">{index + 1}</span>
          <span className="text-sm text-gray-700 leading-relaxed">{step}</span>
        </li>
      ))}
    </ol>
  </section>
);

const ComparisonPage = ({ competitor }) => {
  const data = COMPARISON_DATA[competitor];
  const [plans, setPlans] = useState(COMPARISON_PLANS);
  const [catalogStatus, setCatalogStatus] = useState('fallback');

  useEffect(() => {
    let active = true;

    const loadPlans = async () => {
      try {
        const data = await adminService.getAllPricingTiers();
        if (!active || !Array.isArray(data)) return;
        const activePlans = getCanonicalPricingTiers(data, { includeMissing: false });
        if (activePlans.length > 0) {
          setPlans(activePlans);
          setCatalogStatus('live');
        }
      } catch {
        // Keep the labelled catalog fallback when live pricing cannot be loaded.
      }
    };

    loadPlans();
    return () => {
      active = false;
    };
  }, []);

  return (
  <PageShell
    title={data.title}
    description={data.description}
    path={`/vs/${data.slug}`}
  >
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-5">
          <Icon name="Scale" size={14} />
          <span>Form builder comparison</span>
        </div>
        <h1 className="text-4xl sm:text-6xl font-heading font-bold text-gray-900 leading-tight mb-5">
          WebForms.biz vs {competitor}
        </h1>
        <p className="text-lg text-gray-600 leading-relaxed">
          The right choice depends on your forms, team, and response volume. Here is what WebForms.biz offers so you can compare it with {competitor}’s current product and pricing.
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 sm:p-8 mb-10">
        <p className="text-base sm:text-lg text-gray-700 leading-relaxed">{data.intro}</p>
      </div>

      <DifferentiatorCards plans={plans} catalogStatus={catalogStatus} />
      <ComparisonTable competitor={competitor} plans={plans} catalogStatus={catalogStatus} />
      <PricingComparison competitor={competitor} plans={plans} catalogStatus={catalogStatus} />
      <MigrationGuidance competitor={competitor} steps={data.migration} />

      <div className="rounded-2xl bg-teal-50 border border-teal-100 p-6 sm:p-8 flex flex-col sm:flex-row gap-5 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold text-gray-900 mb-1">Compare WebForms.biz with your own workflow</h2>
          <p className="text-sm text-gray-600">Review every plan’s current limits and features before you decide.</p>
        </div>
        <Link to="/pricing" className="px-5 py-3 rounded-xl bg-[#0e7572] hover:bg-[#0a5f5c] text-white font-semibold whitespace-nowrap">See plans</Link>
      </div>
    </section>
  </PageShell>
  );
};

export const JotformComparisonPage = () => <ComparisonPage competitor="Jotform" />;
export const TypeformComparisonPage = () => <ComparisonPage competitor="Typeform" />;
export const PaperformComparisonPage = () => <ComparisonPage competitor="Paperform" />;
export const TallyComparisonPage = () => <ComparisonPage competitor="Tally" />;